import { BrazilDate } from "../../../../utils/BrazilDate";
import type { ClientePedidoStatusRow } from "./mapPainelParaColunasCliente";

/** Critério de “demorados” para a faixa horizontal inferior (TV balcão). */
export const TV_RETIRADA_MINUTOS_DEMORADO = 30;
export const TV_RETIRADA_MS_DEMORADO = TV_RETIRADA_MINUTOS_DEMORADO * 60 * 1000;

function dataCalendarioBrasilDoIso(iso: string | undefined): string | null {
  if (!iso?.trim()) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return BrazilDate.formatISO(d);
}

/**
 * Minutos inteiros decorridos desde `prontoParaRetiradaEm` (backend = `PRODUCOES.data_fim`)
 * até `agora` (relógio real do cliente).
 */
export function minutosDesdeProntoParaRetirada(
  iso: string | undefined,
  agora: Date
): number | null {
  if (!iso?.trim()) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((agora.getTime() - t) / 60000));
}

export type ParticaoProntosRetiradaTv = {
  /** Prontos hoje — modal / conjunto para transição Prep → Pronto. */
  todosProntosHoje: ClientePedidoStatusRow[];
  /** “Pronto para retirar” (lista verde): até `TV_RETIRADA_MINUTOS_DEMORADO` desde `data_fim`. */
  prontosRecentesHoje: ClientePedidoStatusRow[];
  /**
   * Rodapé (demorados): só itens já na API de prontos há mais que `TV_RETIRADA_MINUTOS_DEMORADO` desde `data_fim`.
   */
  prontosDemorandoHoje: ClientePedidoStatusRow[];
};

/**
 * Particiona só itens que já vêm da API como **prontos** (`takeouts-prontos` / `PRODUCOES.data_fim`).
 * Dia civil America/São_Paulo; “em preparo” nunca entra aqui.
 */
export function partitionProntosRetiradaTv(
  prontos: ClientePedidoStatusRow[],
  agora: Date
): ParticaoProntosRetiradaTv {
  const diaHojeBr = BrazilDate.formatISO(agora);
  const todosProntosHoje = prontos.filter((r) => {
    const d = dataCalendarioBrasilDoIso(r.prontoParaRetiradaEm);
    return d !== null && d === diaHojeBr;
  });

  const prontosRecentesHoje: ClientePedidoStatusRow[] = [];
  const prontosDemorandoHoje: ClientePedidoStatusRow[] = [];

  for (const r of todosProntosHoje) {
    const iso = r.prontoParaRetiradaEm;
    const liberadoMs = iso ? new Date(iso).getTime() : NaN;
    const elapsedMs = Number.isNaN(liberadoMs)
      ? NaN
      : agora.getTime() - liberadoMs;

    if (Number.isNaN(liberadoMs)) {
      prontosRecentesHoje.push(r);
      continue;
    }

    // Só vai ao rodapé após ultrapassar o limite estrito (rótulo “mais de N min”).
    if (elapsedMs > TV_RETIRADA_MS_DEMORADO) {
      prontosDemorandoHoje.push(r);
    } else {
      prontosRecentesHoje.push(r);
    }
  }

  /** TV: mais recentemente liberados no topo, mais antigos abaixo. */
  function porProntoMaisRecentePrimeiro(
    u: ClientePedidoStatusRow,
    v: ClientePedidoStatusRow
  ) {
    const isoU = u.prontoParaRetiradaEm?.trim();
    const isoV = v.prontoParaRetiradaEm?.trim();
    const tu = isoU ? new Date(isoU).getTime() : Number.NEGATIVE_INFINITY;
    const tv = isoV ? new Date(isoV).getTime() : Number.NEGATIVE_INFINITY;
    const nu = Number.isNaN(tu) ? Number.NEGATIVE_INFINITY : tu;
    const nv = Number.isNaN(tv) ? Number.NEGATIVE_INFINITY : tv;
    if (nv !== nu) return nv - nu;
    return u.displayId - v.displayId;
  }

  prontosRecentesHoje.sort(porProntoMaisRecentePrimeiro);

  return {
    todosProntosHoje,
    prontosRecentesHoje,
    prontosDemorandoHoje,
  };
}
